import { db } from './config'

// AI-CONTEXT: See docs/features/public-livereport-data.md (fase 5: definities)
//
// De definities achter een gedeeld rapport (vragenlijsten, vragen, opties,
// onderzoeken, meetmomenten, activiteiten, outputs, effecten, formulieren,
// doelgroepen) komen mee met getPublicLiveReport2. Zolang de openbare
// rapportpagina open is, lezen de widgets en hun hooks ze hieruit in plaats van
// uit Firestore — anoniem mag dat niet meer.
//
// Eén centrale schakel, zodat de tientallen leesplekken in widgets en grafieken
// niet elk een eigen openbare tak nodig hebben: `useFirestoreQuery` en de
// directe lezen gebruiken `definitionCollection(naam)` in plaats van
// `db.collection(naam)`.
// Buiten de openbare rapportpagina is er geen set en verandert er niets.
//
// Controlemodus (`?definitiecheck=1` op de openbare link): er wordt gewoon uit
// Firestore gelezen, en elke definitie die Firestore teruggeeft maar die niet
// in de meegestuurde set zit, komt in de console. Zo is te zien of de server
// iets mist, vóórdat de regels dichtgaan.

export const DEFINITION_COLLECTIONS = new Set([
    'Questionnaires', 'QuestionnaireFields', 'MultipleQuestionOptions', 'MatrixQuestionRows',
    'MatrixQuestionColumns', 'MeasureMoments', 'Research', 'EventForms', 'EventFields', 'Outputs',
    'Activities', 'Stakeholders', 'SubTargetgroups', 'OutputEffects', 'CustomEventFields',
])

/** @type {{ byCollection: Record<string, Array<object>>, check: boolean } | null} */
let active = null
/** In controlemodus: per collectie de id's die Firestore gaf maar de set niet had. */
const misses = {}

/** Een tijdstempel zoals res.json hem stuurt ({ _seconds, _nanoseconds }) weer bruikbaar maken. */
const revive = (value) => {
    if (Array.isArray(value)) return value.map(revive)
    if (value && typeof value === 'object') {
        const seconds = value._seconds ?? value.seconds
        const nanos = value._nanoseconds ?? value.nanoseconds
        if (typeof seconds === 'number' && typeof nanos === 'number' && Object.keys(value).length === 2) {
            const ms = seconds * 1000 + Math.floor(nanos / 1e6)
            return { seconds, nanoseconds: nanos, toMillis: () => ms, toDate: () => new Date(ms) }
        }
        return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, revive(v)]))
    }
    return value
}

/**
 * Zet de definities van het gedeelde rapport neer (of null om ze weg te halen).
 *
 * @param {Record<string, Array<object>>|null} definitions - Uit getPublicLiveReport2
 * @param {{ check?: boolean }} [options]
 */
export const setPublicDefinitions = (definitions, { check = false } = {}) => {
    active = definitions
        ? { byCollection: Object.fromEntries(Object.entries(definitions).map(([c, docs]) => [c, revive(docs || [])])), check }
        : null
}

const isOn = (collection) => Boolean(active) && DEFINITION_COLLECTIONS.has(collection)

const ms = (v) => (v && typeof v.toMillis === 'function' ? v.toMillis() : v)
const matches = (doc, [field, op, value]) => {
    const v = field === '__name__' ? doc.docid : doc[field]
    switch (op) {
        case '==': return ms(v) === ms(value)
        case '!=': return v !== undefined && ms(v) !== ms(value)
        case 'in': return Array.isArray(value) && value.includes(v)
        case 'not-in': return v !== undefined && Array.isArray(value) && !value.includes(v)
        case 'array-contains': return Array.isArray(v) && v.includes(value)
        case 'array-contains-any': return Array.isArray(v) && Array.isArray(value) && v.some((x) => value.includes(x))
        case '<': return v !== undefined && ms(v) < ms(value)
        case '<=': return v !== undefined && ms(v) <= ms(value)
        case '>': return v !== undefined && ms(v) > ms(value)
        case '>=': return v !== undefined && ms(v) >= ms(value)
        default: return false
    }
}

/**
 * Dezelfde vraag als Firestore, maar op de meegestuurde set. Zoals Firestore
 * valt een document zonder het `orderBy`-veld weg.
 *
 * @param {string} collection
 * @param {Array<[string, string, *]>} where
 * @param {Array<[string, 'asc'|'desc']>} [orderBys]
 * @param {number|null} [limit]
 * @returns {Array<object>} Documenten met `docid`
 */
export const queryPublicDefinitions = (collection, where, orderBys = [], limit = null) => {
    let docs = (active?.byCollection[collection] || []).filter((d) => where.every((c) => matches(d, c)))
    orderBys.forEach(([field]) => { docs = docs.filter((d) => d[field] !== undefined && d[field] !== null) })
    if (orderBys.length) {
        docs = [...docs].sort((a, b) => {
            for (const [field, dir] of orderBys) {
                const x = ms(a[field]); const y = ms(b[field])
                if (x === y) continue
                const r = x < y ? -1 : 1
                return dir === 'desc' ? -r : r
            }
            return 0
        })
    }
    return limit ? docs.slice(0, limit) : docs
}

/** Controlemodus: meld wat Firestore gaf en de set niet heeft. */
export const checkPublicDefinitions = (collection, firestoreDocs, where) => {
    if (!isOn(collection) || !active.check) return
    const known = new Set((active.byCollection[collection] || []).map((d) => d.docid))
    const missing = firestoreDocs.map((d) => d.docid).filter((id) => !known.has(id))
    if (!missing.length) return
    misses[collection] = [...new Set([...(misses[collection] || []), ...missing])]
    // eslint-disable-next-line no-console
    console.warn(`[definitiecheck] ${collection}: ${missing.length} niet in de set`, { missing, where, alleTotNu: misses })
}

const snapshotOf = (docs) => {
    const wrapped = docs.map((d) => ({ id: d.docid, exists: true, data: () => d }))
    return { docs: wrapped, empty: wrapped.length === 0, size: wrapped.length, forEach: (fn) => wrapped.forEach(fn) }
}

/** Een alleen-lezen nabootsing van een Firestore-query op de set. */
const fakeQuery = (collection, where, orderBys, limit) => ({
    where: (field, op, value) => fakeQuery(collection, [...where, [field, op, value]], orderBys, limit),
    orderBy: (field, dir = 'asc') => fakeQuery(collection, where, [...orderBys, [field, dir]], limit),
    limit: (n) => fakeQuery(collection, where, orderBys, n),
    get: async () => snapshotOf(queryPublicDefinitions(collection, where, orderBys, limit)),
    onSnapshot: (next) => {
        Promise.resolve().then(() => next(snapshotOf(queryPublicDefinitions(collection, where, orderBys, limit))))
        return () => {}
    },
})

/** Om een echte query heen, in controlemodus: vergelijk de uitkomst met de set. */
const checkedQuery = (collection, query, where) => ({
    where: (field, op, value) => checkedQuery(collection, query.where(field, op, value), [...where, [field, op, value]]),
    orderBy: (...args) => checkedQuery(collection, query.orderBy(...args), where),
    limit: (n) => checkedQuery(collection, query.limit(n), where),
    get: async (...args) => {
        const snap = await query.get(...args)
        checkPublicDefinitions(collection, snap.docs.map((d) => ({ docid: d.id })), where)
        return snap
    },
    onSnapshot: (next, ...rest) => query.onSnapshot((snap) => {
        checkPublicDefinitions(collection, snap.docs.map((d) => ({ docid: d.id })), where)
        next(snap)
    }, ...rest),
})

/**
 * In plaats van `db.collection(naam)` voor een definitiecollectie die gelezen
 * wordt. Op de openbare rapportpagina een nabootsing op de meegestuurde set;
 * elders gewoon `db.collection(naam)`. Alleen voor lezen: schrijven gaat altijd
 * via `db`.
 *
 * @param {string} collection
 * @param {{ collection: (name: string) => object }} [source] - Waar zonder set gelezen
 *   wordt; standaard `db`. Voor helpers die hun Firestore als parameter krijgen.
 */
export const definitionCollection = (collection, source = db) => {
    if (!isOn(collection)) return source.collection(collection)
    if (active.check) return checkedQuery(collection, source.collection(collection), [])
    return fakeQuery(collection, [], [], null)
}
